import { CategorySettings, convertToPixelPosition, OnMouseDownHandler } from '@lessonup/pin-renderer';
import { CategoryAreaPinComponent } from '@lessonup/pins-shared';
import { styled } from '@lessonup/ui-components';
import React from 'react';
import { CategoryGrippies } from './CategoryGrippies';

interface CategoryAreaControlBoxProps {
  active: CategoryAreaPinComponent;
  scale: number;
  onMouseDown: OnMouseDownHandler;
  isPinComponentHighlighted: boolean;
  activeCategoryId?: string;
}

export const CategoryAreaControlBox: React.FC<CategoryAreaControlBoxProps> = ({
  active,
  scale,
  onMouseDown,
  isPinComponentHighlighted,
  activeCategoryId,
}) => {
  const settings = active.settings;
  const { layoutMode, categories } = settings;

  if (layoutMode !== 'CUSTOM') {
    return null;
  }

  const containerBox =
    active.layout.type === 'BOX'
      ? active.layout
      : {
          position: { x: 0, y: 0 },
          size: { width: 400, height: 300 },
          rotation: 0,
        };

  const containerSize = {
    width: containerBox.size.width,
    height: containerBox.size.height,
  };

  const convertToPixelPositionWithScale = (category: CategorySettings) => {
    const pixelCategory = convertToPixelPosition(category, containerSize);

    return {
      ...pixelCategory,
      layout: {
        ...pixelCategory.layout,
        position: {
          x: pixelCategory.layout.position.x * scale,
          y: pixelCategory.layout.position.y * scale,
        },
        size: {
          width: pixelCategory.layout.size.width * scale,
          height: pixelCategory.layout.size.height * scale,
        },
      },
    };
  };

  return (
    <StyledCategoryAreaControlBox
      style={{
        width: '100%',
        height: '100%',
      }}
    >
      {categories.map((category: CategorySettings) => {
        const pixelCategory = convertToPixelPositionWithScale(category);
        const isCategoryActive = activeCategoryId === category.id;
        const isCategoryHovered = isPinComponentHighlighted && isCategoryActive;

        const categoryWithBoxLayout: CategorySettings = {
          id: pixelCategory.id,
          label: pixelCategory.label,
          layout: {
            type: 'BOX',
            position: pixelCategory.layout.position,
            size: pixelCategory.layout.size,
            rotation: pixelCategory.layout.rotation,
          },
        };

        return (
          <StyledCategoryControl
            key={category.id}
            style={{
              position: 'absolute',
              left: pixelCategory.layout.position.x,
              top: pixelCategory.layout.position.y,
              width: pixelCategory.layout.size.width,
              height: pixelCategory.layout.size.height,
              transform: `rotate(${pixelCategory.layout.rotation}deg)`,
              transformOrigin: 'center',
            }}
          >
            {isCategoryActive && (
              <CategoryGrippies
                componentId={active.id}
                category={categoryWithBoxLayout}
                onMouseDown={onMouseDown}
                isCategoryHovered={isCategoryHovered}
              />
            )}
          </StyledCategoryControl>
        );
      })}
    </StyledCategoryAreaControlBox>
  );
};

const StyledCategoryAreaControlBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`;

const StyledCategoryControl = styled.div`
  pointer-events: none;

  /* Enable pointer events only for grippies */
  > * {
    pointer-events: auto;
  }
`;
